<template>
  <Modal
    class="modal-delete"
    :is-displayed="status === 'displayed' || status === 'in-progress'"
    @close="close"
  >
    <template #title>
      <h3 class="modal-title">Delete Routine account</h3>
    </template>
    <template v-if="status === 'displayed'">
      <BaseAlert state="error">
        <div class="alert-content">
          <p>
            <strong>This action will:</strong>
          </p>
          <ul>
            <li>Delete all of your data.</li>
            <li>Cancel your subscription (if any).</li>
            <li>Unsubscribe from all email communications.</li>
          </ul>
        </div>
      </BaseAlert>
      <div class="text">
        <p>
          Type <em> {{ mainEmail }} </em>
          <button
            class="copy-email"
            title="Copy email address"
            @click="copyEmail"
          >
            <CopyIcon />
          </button>
          to confirm.
        </p>
      </div>
      <input v-model="confirm" class="confirm" placeholder="Type…" />
      <div class="buttons">
        <BaseButton kind="main" type="tertiary" text="Cancel" @click="close" />
        <BaseButton
          kind="main"
          type="primary"
          tint="red"
          :state="deleteAvailable ? 'default' : 'disabled'"
          text="Delete"
          @click="submit"
        >
          <template #icon>
            <CloseIcon />
          </template>
        </BaseButton>
      </div>
    </template>
    <template v-else>
      <div class="loader-container">
        <p>Deletion in progress, do not close this window.</p>
        <Loader>
          <Spinner />
        </Loader>
      </div>
    </template>
  </Modal>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";

import { Profile } from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import Modal from "@/components/Modal.vue";
import { useReleaser } from "@/ref";

export default defineComponent({
  components: {
    BaseAlert,
    BaseButton,
    CloseIcon,
    CopyIcon,
    Modal,
    Loader,
  },
  props: {
    status: {
      type: String as PropType<"displayed" | "in-progress" | "hidden">,
      default: "hidden",
    },
  },
  emits: {
    close: () => true,
    deleteAccount: () => true,
  },
  async setup(_props, { emit }) {
    const controller = inject.controller();
    const releaser = useReleaser();
    const identities = await releaser.add(controller.auth.profiles());
    const mainEmail = computed(
      () => identities.value.find((identity: Profile) => identity.main)?.email
    );
    const submit = () => emit("deleteAccount");
    const close = () => emit("close");
    const confirm = ref<string | null>(null);
    const deleteAvailable = computed(() => confirm.value === mainEmail.value);
    const copyEmail = async () => {
      const email = mainEmail.value;
      if (!email) return;
      await copy(email);
      controller.notifications.notify(`${email} copied to clipboard`);
    };
    return {
      close,
      confirm,
      copyEmail,
      deleteAvailable,
      mainEmail,
      submit,
    };
  },
});
</script>
<style scoped lang="scss">
.modal-delete {
  :deep(.modal) {
    padding: 18px 19px;
  }

  &.modal-bg .modal .modal-wrapper .title {
    .modal-title {
      margin: 0;
      text-transform: uppercase;
      @extend %font-small-regular;
      @include themed() {
        color: t($grey-50);
      }
    }
  }
  .text {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    margin-bottom: 16px;
    p {
      @extend %font-caption-regular;
      margin: 0;
      strong {
        @extend %font-caption-medium;
      }
      em {
        font-style: normal;
        @include themed() {
          color: t($secondary);
        }
      }
    }

    .copy-email {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      vertical-align: middle;
      width: 20px;
      height: 20px;
      padding: 0;
      border: none;
      border-radius: 3px;
      background: none;
      cursor: pointer;

      svg {
        width: 14px;
        height: 14px;
      }

      :deep(.stroke) {
        @include transition(stroke 100ms ease-out);
        @include themed() {
          stroke: t($grey-50);
        }
      }

      &:hover {
        @include themed() {
          background-color: t($grey-05);
        }

        :deep(.stroke) {
          @include themed() {
            stroke: t($grey-80);
          }
        }
      }
    }
  }
  input.confirm {
    padding: 12px 16px;
    @extend %font-body-regular;
    outline: none;
    border-radius: 3px;
    @include themed() {
      background-color: t($bg);
      color: t($primary);
      border: 1px solid t($grey-30);
    }
  }
  .buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 36px;
    align-items: center;
    column-gap: 16px;
  }
  .alert-content {
    ul {
      list-style: disc;
      padding-inline-start: 20px;
    }
  }
  .loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 16px;
    padding: 32px 0;

    p {
      @extend %font-body-medium;
      text-align: center;
      margin: 0;
      @include themed() {
        color: t($grey-70);
      }
    }

    :deep(.container) {
      height: 48px;
      width: 48px;
      margin: 0 auto;

      .spinner {
        width: 32px;
        height: 32px;
        border-width: 3px;
      }
    }
  }
}
</style>
